
import { FC, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Source } from "@/services/frank/types";
import { DocumentInfo, findMatchingDocument, createDocumentSignedUrl } from "./DocumentMatcher";

interface SourceItemProps {
  source: Source;
  documents: DocumentInfo[];
  loadingDocuments: boolean;
}

const SourceItem: FC<SourceItemProps> = ({
  source,
  documents,
  loadingDocuments
}) => {
  const [isOpening, setIsOpening] = useState(false);
  const matchingDocument = source.source ? findMatchingDocument(source.source, documents) : undefined;

  const handleOpenDocument = async () => {
    if (!matchingDocument) return;
    try {
      setIsOpening(true);
      const signedUrl = await createDocumentSignedUrl(matchingDocument);
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error opening document:', error);
    } finally {
      setIsOpening(false);
    }
  };

  // Function to safely render HTML content with <strong> tags
  const renderFormattedText = (text: string) => {
    // Split by <strong> and </strong> tags to create segments
    const segments = text.split(/(<strong>|<\/strong>)/g);
    return segments.map((segment, index) => {
      if (segment === '<strong>') {
        // This is an opening tag, skip it
        return null;
      } else if (segment === '</strong>') {
        // This is a closing tag, skip it
        return null;
      } else if (index > 0 && segments[index - 1] === '<strong>' && segments[index + 1] === '</strong>') {
        // This segment is between <strong> tags, render as bold
        return <strong key={index}>{segment}</strong>;
      } else {
        // Regular text
        return <span key={index}>{segment}</span>;
      }
    });
  };

  return <div className="bg-secondary-foreground/10 p-2 rounded-md mt-2">
      <p className="font-medium text-justify">"
        {renderFormattedText(source.text)}
      "</p>
      <div className="mt-1 opacity-70 italic flex flex-col items-center">
        {loadingDocuments ? <p className="flex items-center text-xs text-center">- {source.source} <span className="ml-2 text-xs">(loading...)</span></p> : matchingDocument ? <button 
          onClick={handleOpenDocument} 
          disabled={isOpening} 
          className="flex items-center justify-center hover:underline hover:opacity-100 transition-colors font-normal text-center"
        >
            <span className="text-center">- {source.source}</span>
            <ExternalLink className="h-3 w-3 ml-1" />
            {isOpening && <span className="ml-2 text-xs">(opening...)</span>}
          </button> : <p className="text-center">- {source.source}</p>}
        {source.page && <p className="text-xs text-center">Page: {source.page}</p>}
      </div>
    </div>;
};

export default SourceItem;
